import { requireService } from '@profgeosoft/di-ez';
import { TableMultiSelect, TableSelect } from '@repo/go-table';
import { reaction } from 'mobx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import type { GridRenderEditCellParams } from '@repo/go-table';
import type { ReactElement } from 'react';
import type { TOption } from 'src/packages/types';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TComboBoxView, TMultiComboBoxView } from 'src/services/directory-service/types';

import { ComboboxDropdown } from '../../../combobox-dropdown';

import styles from './edit-mode-combobox.module.scss';

type Props = {
  params: GridRenderEditCellParams<DirectoryRecord>;
  record: DirectoryRecord;
  controlView: TComboBoxView | TMultiComboBoxView;
  optionsService: OptionsService;
  onCreateNewRecord: VoidFunction;
  onChange(value: unknown): void;
  onBlur(): void;
};

// TODO refactor:
// разделить на 2 компонента - MultiCombobox и Combobox
export const EditComboboxCell = wrap<Props>(function EditComboboxCell({
  params,
  record,
  controlView,
  optionsService,
  onChange,
  onBlur,
  onCreateNewRecord,
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const directoriesListService = requireService('directoriesListService');
  const [isOpen, setIsOpen] = useState(params.hasFocus);
  // TODO: поправить костыль
  const [isLoading, setIsLoading] = useState(controlView.refQuery ? true : false);
  const controlValue = params.row.data[controlView.fieldId];
  const fieldRulesData = record.fieldRulesData[controlView.fieldId];
  const fieldErrors = record.fieldsErrors[controlView.fieldId];

  const isEditable = useMemo(() => {
    return directoriesListService.getObjectType(controlView.refObjectType ?? '')?.isEditingAvailable ?? false;
  }, [directoriesListService, controlView.refObjectType]);

  const [internalOptions, setInternalOptions] = useState<TOption<string | number>[]>(() => {
    if (controlView.refQuery) {
      return [];
    }

    return optionsService.options[controlView.fieldId] ?? [];
  });

  const dropDownRender = useMemo(() => {
    if (!onCreateNewRecord || !isEditable) {
      return undefined;
    }

    return function dr(menu: ReactElement) {
      return (
        <ComboboxDropdown onButtonClick={onCreateNewRecord} onClose={() => setIsOpen(false)}>
          {menu}
        </ComboboxDropdown>
      );
    };
  }, [isEditable, onCreateNewRecord]);

  useEffect(() => {
    if (!controlView.refQuery) {
      return;
    }

    const abortController = new AbortController();

    setIsLoading(true);

    optionsService
      // TODO: избавиться от params.row.data, использовать record.data, т.е. находить инстанс Record и брать data из него
      .getOptionsByRefQuery(controlView, params.row.data, abortController.signal)
      .then((options) => setInternalOptions(options))
      .finally(() => setIsLoading(false));

    return () => abortController.abort();
  }, [controlView, controlView.refQuery, optionsService, params.row.data]);

  useEffect(() => {
    return reaction(
      () => optionsService.options[controlView.fieldId] ?? [],
      (options) => setInternalOptions(options),
      { fireImmediately: true },
    );
  }, [controlView.fieldId, optionsService.options]);

  const isOpened = isOpen && !isLoading && !fieldRulesData?.isDisabled && !fieldRulesData?.isEnteringBlocked;

  const filteredInternalOptions = !fieldRulesData?.allowedOptions
    ? internalOptions
    : internalOptions.filter((opt) => fieldRulesData.allowedOptions![opt.value]);

  const filteredArchivedOptions = !fieldRulesData?.allowedOptions
    ? optionsService.archivedOptions[controlView.fieldId]
    : optionsService.archivedOptions[controlView.fieldId]?.filter((opt) => fieldRulesData.allowedOptions![opt.value]);

  if (controlView.control === 'MultiComboBox') {
    const multiValue: TOption<string | number>[] = [];

    if (Array.isArray(controlValue) && !isLoading) {
      for (const rawValue of controlValue) {
        const value =
          filteredInternalOptions.find((o) => o.value === rawValue) ||
          filteredArchivedOptions?.find((o) => o.value === rawValue);

        if (hasValue(value)) {
          multiValue.push(value);
        }
      }

      if (controlValue.length !== multiValue.length) {
        onChange(multiValue.map((v) => v.value));
      }
    }

    return (
      <div ref={ref} className={styles.wrapper}>
        <TableMultiSelect
          cellParams={params}
          value={multiValue}
          open={isOpened}
          className={styles.select}
          sortOptions
          placeholder={t('common:placeholders.chooseValue')}
          status={fieldErrors?.length ? 'error' : undefined}
          loading={isLoading}
          options={filteredInternalOptions}
          optionFilterProp="label"
          dropdownMatchSelectWidth={false}
          onDropdownVisibleChange={(open) => setIsOpen(open)}
          popupClassName={styles.popupWrapper}
          dropdownRender={dropDownRender}
          onChange={onChange}
          onBlur={onBlur}
          allowClear
        />
      </div>
    );
  }

  const valueOption =
    filteredInternalOptions?.find((o) => o.value === controlValue) ||
    filteredArchivedOptions?.find((o) => o.value === controlValue);

  if (!valueOption && hasValue(controlValue) && !isLoading) {
    onChange(null);
  }

  return (
    <div className={styles.wrapper}>
      <TableSelect
        cellParams={params}
        value={valueOption}
        open={isOpened}
        className={styles.select}
        optionFilterProp="label"
        loading={isLoading}
        filterOption
        showSearch
        sortOptions
        placeholder={t('common:placeholders.chooseValue')}
        status={fieldErrors?.length ? 'error' : undefined}
        options={filteredInternalOptions}
        dropdownMatchSelectWidth={false}
        onDropdownVisibleChange={(open) => setIsOpen(open)}
        popupClassName={styles.popupWrapper}
        dropdownRender={dropDownRender}
        onChange={onChange}
        onSelect={onChange}
        onBlur={onBlur}
        allowClear
      />
    </div>
  );
});
